import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const LOGO_H =
  "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-12/prod_agent_ead185b1-37d3-41b0-8463-37e266b01c59/Logo_Horizontal_Transparent_eeb37a07dc644f8290d0f96bd0f092d9.png";

/* @section: header */
export default function Header() {
  const { lang, setLang, t } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const navigate = useNavigate();

  const navLinks = [
    { labelEn: "Home", labelKo: "홈", to: "/" },
    {
      labelEn: "Services",
      labelKo: "서비스",
      to: "/services",
      children: [
        { labelEn: "Tax Planning", labelKo: "절세 플랜", to: "/tax-planning" },
        { labelEn: "Individual Tax Return", labelKo: "개인 세금보고", to: "/individual-tax" },
        { labelEn: "Business Formation", labelKo: "회사 설립", to: "/business-formation" },
        { labelEn: "IRS Notices", labelKo: "IRS 통지서 대응", to: "/irs-notices" },
      ],
    },
    { labelEn: "About", labelKo: "소개", to: "/about" },
    { labelEn: "Resources", labelKo: "세금 팁", to: "/resources" },
    { labelEn: "Contact", labelKo: "문의", to: "/contact" },
  ];

  return (
    <header className="sticky top-0 z-50 w-full bg-white border-b border-border shadow-sm">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="flex items-center justify-between h-32 md:h-36">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0" onClick={() => setMenuOpen(false)}>
            <img
              src={LOGO_H}
              alt="NexBiz Tax Advisor"
              className="h-28 md:h-32 w-auto object-contain"
              style={{ filter: "none" }}
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) =>
              link.children ? (
                <div
                  key={link.to}
                  className="relative group"
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                >
                  <button
                    className="flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium text-foreground hover:text-primary hover:bg-muted transition-colors"
                    onClick={() => navigate(link.to)}
                  >
                    {t(link.labelEn, link.labelKo)}
                    <ChevronDown className="w-3.5 h-3.5 mt-0.5" />
                  </button>
                  {servicesOpen && (
                    <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-border rounded-lg shadow-lg py-1 z-50">
                      {link.children.map((child) => (
                        <Link
                          key={child.to}
                          to={child.to}
                          className="block px-4 py-2.5 text-sm text-foreground hover:text-primary hover:bg-muted transition-colors"
                          onClick={() => setServicesOpen(false)}
                        >
                          {t(child.labelEn, child.labelKo)}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === "/"}
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? "text-primary bg-muted"
                        : "text-foreground hover:text-primary hover:bg-muted"
                    }`
                  }
                >
                  {t(link.labelEn, link.labelKo)}
                </NavLink>
              )
            )}
          </nav>

          {/* Right side: Lang toggle + CTA + Hamburger */}
          <div className="flex items-center gap-2">
            {/* Language Toggle */}
            <div className="flex items-center border border-border rounded-full overflow-hidden text-xs font-semibold">
              <button
                onClick={() => setLang("en")}
                className={`px-2.5 py-1 transition-colors ${
                  lang === "en"
                    ? "bg-primary text-primary-foreground"
                    : "bg-white text-muted-foreground hover:bg-muted"
                }`}
              >
                EN
              </button>
              <button
                onClick={() => setLang("ko")}
                className={`px-2.5 py-1 transition-colors ${
                  lang === "ko"
                    ? "bg-primary text-primary-foreground"
                    : "bg-white text-muted-foreground hover:bg-muted"
                }`}
              >
                한국어
              </button>
            </div>

            {/* CTA Button (desktop) */}
            <Link
              to="/contact"
              className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-opacity shadow-sm"
            >
              {t("Schedule Consultation", "상담 예약")}
            </Link>

            {/* Hamburger */}
            <button
              className="lg:hidden p-2 rounded-md text-foreground hover:bg-muted transition-colors"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="lg:hidden bg-white border-t border-border px-4 py-3 space-y-1">
          {navLinks.map((link) => (
            <div key={link.to}>
              <NavLink
                to={link.to}
                end={link.to === "/"}
                className={({ isActive }) =>
                  `block px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "text-primary bg-muted"
                      : "text-foreground hover:text-primary hover:bg-muted"
                  }`
                }
                onClick={() => setMenuOpen(false)}
              >
                {t(link.labelEn, link.labelKo)}
              </NavLink>
              {link.children && (
                <div className="ml-4 mt-1 space-y-0.5">
                  {link.children.map((child) => (
                    <Link
                      key={child.to}
                      to={child.to}
                      className="block px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-primary hover:bg-muted transition-colors"
                      onClick={() => setMenuOpen(false)}
                    >
                      {t(child.labelEn, child.labelKo)}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="pt-2 pb-1">
            <Link
              to="/contact"
              className="block w-full text-center px-4 py-2.5 rounded-md text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
              onClick={() => setMenuOpen(false)}
            >
              {t("Schedule Consultation", "상담 예약")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
